/**
 * A `fetch` wrapper that bounds how long any Supabase request can run.
 *
 * WHY THIS EXISTS – the auth lock holder problem:
 * supabase-js serializes all access-token operations through a single lock
 * (`lock:sb-<ref>-auth-token`; per tab since 2026-09-24, cross-tab Web Lock
 * before – see client.ts). gotrue holds that lock for
 * the ENTIRE duration of an `/auth/v1/token` (refresh) or `/auth/v1/user`
 * request. The browser's default `fetch` has no timeout, so on mobile a
 * backgrounded / network-switched socket can hang for minutes-to-hours with the
 * connection open but no response. While it hangs, the lock is held, and EVERY
 * authenticated query in the tab aborts at the lock-acquire timeout – the
 * whole authenticated app goes dark until the OS finally kills the socket.
 *
 * The lock-acquire timeout only bounds the *waiters*; nothing bounds the
 * *holder*. This wrapper is what bounds the holder: a hung auth request is
 * aborted after `timeoutMs`, releasing the lock so the next auto-refresh can
 * recover. See the 2026-06-11 Web Lock incident.
 */

export interface TimeoutFetchOptions {
  /** Abort any non-storage request that runs longer than this. */
  timeoutMs?: number
  /**
   * Called when OUR timeout aborts an `/auth/v1/*` request. A hung auth request
   * is the holder that jams the lock app-wide, so this is the signal to offer
   * the user session recovery (a reload tears down the context and frees the
   * lock). Not called for caller-initiated aborts or non-auth timeouts.
   */
  onAuthTimeout?: () => void
  /** Injectable for tests; defaults to the global `fetch`. */
  fetchImpl?: typeof fetch
}

const DEFAULT_TIMEOUT_MS = 20_000

function getUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.href
  return input.url
}

export function createTimeoutFetch(options: TimeoutFetchOptions = {}): typeof fetch {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, onAuthTimeout } = options
  // Resolve the global lazily so importing this module never requires `fetch`
  // to exist at module-eval time (e.g. in non-browser test contexts).
  const baseFetch: typeof fetch =
    options.fetchImpl ?? ((input, init) => fetch(input, init))

  return (input, init) => {
    const url = getUrl(input as RequestInfo | URL)

    // Storage transfers (uploads/downloads) can legitimately run long on slow
    // mobile links and never touch the auth lock – never cap them.
    if (url.includes('/storage/v1/')) {
      return baseFetch(input, init)
    }

    const controller = new AbortController()
    let timedOut = false
    const timeoutId = setTimeout(() => {
      timedOut = true
      controller.abort(new DOMException('Supabase request timed out', 'TimeoutError'))
    }, timeoutMs)

    // Honour a caller-provided signal (supabase-js `.abortSignal()`, our own
    // AbortControllers) so query cancellation still works.
    const callerSignal = init?.signal
    if (callerSignal) {
      if (callerSignal.aborted) {
        controller.abort(callerSignal.reason)
      } else {
        callerSignal.addEventListener(
          'abort',
          () => controller.abort(callerSignal.reason),
          { once: true }
        )
      }
    }

    return baseFetch(input, { ...init, signal: controller.signal })
      .catch((err) => {
        // Only our own timeout on an auth request signals a jammed lock holder.
        if (timedOut && url.includes('/auth/v1/')) {
          onAuthTimeout?.()
        }
        throw err
      })
      .finally(() => clearTimeout(timeoutId))
  }
}
